import { useCallback } from "react";
import { toastManager } from "@salestrak/ui";

import { useIssueViewMutations } from "#app/hooks/issue-view/use-issue-view-mutations";
import { AnalyticsContext, FollowUpEvent, useAnalytics } from "#app/lib/analytics";
import type { IssueAgeInput } from "#app/lib/data";
import type { UpdateCycle } from "#app/lib/project-updates";
import { UNDO_TOAST_SHORTCUT, UNDO_TOAST_TIMEOUT_MS } from "#app/lib/undo-shortcut";
import { toastMutationError } from "#app/lib/utils/toast-errors";

/** A cycle as a stable analytics value: "2-weeks". */
function cycleKey(cycle: UpdateCycle): string {
  return `${cycle.amount}-${cycle.step}`.toLowerCase();
}

/**
 * The per-Project cycle writes every Follow up surface shares: give the
 * Project its own cycle (also what starts a Follow up on a Project whose
 * rungs below are off), hand it back to the default, or end it. Toasts live
 * here, in the mutation-hook layer — callers fire and let the cache re-render.
 */
export function useProjectCycleMutations(issueId: string): {
  /** Writes the cycle. `started` is the Start dialog's write, which shows no
   *  success toast (the dialog's own close confirms it), so this only names
   *  the act in its failure toast. */
  saveCycle: (
    cycle: UpdateCycle,
    options?: {
      started?: boolean;
      /** Start only: the surface that opened the Start dialog. */
      via?: string;
      /** Start only: the rep kept the cycle the dialog pre-answered. */
      cycleIsDefault?: boolean;
    },
  ) => Promise<void>;
  resetCycle: () => Promise<void>;
  /** Turns updates off for this Project alone. The toast offers Undo, which
   *  restores `previous` (the Project's own cycle) or the inherited default. */
  endFollowUp: (previous: UpdateCycle | null) => Promise<void>;
} {
  // The lifecycle has its own `follow_up.*` events below; the edit event only
  // names the write.
  const { updateIssue } = useIssueViewMutations({ updateIssueLabel: "follow_up" });
  const { track } = useAnalytics();

  const write = useCallback(
    async (age: IssueAgeInput, failure: string) => {
      try {
        const result = await updateIssue({
          variables: { issueId, input: { age } },
        });
        const payload = result.data?.updateIssue;
        if (!payload || payload.__typename !== "Issue") {
          throw new Error(
            payload && "message" in payload ? payload.message : "Update failed",
          );
        }
        return true;
      } catch (error) {
        toastMutationError(error, failure);
        return false;
      }
    },
    [issueId, updateIssue],
  );

  const saveCycle = useCallback(
    async (
      cycle: UpdateCycle,
      options?: { started?: boolean; via?: string; cycleIsDefault?: boolean },
    ) => {
      const ok = await write(
        { cycle: { amount: cycle.amount, step: cycle.step } },
        options?.started
          ? "Failed to start the Follow up"
          : "Failed to save the follow up cycle",
      );
      if (!ok) return;
      if (options?.started) {
        track({
          context: AnalyticsContext.FollowUp,
          event: FollowUpEvent.Start,
          data: {
            target: "project",
            cycle: cycleKey(cycle),
            cycleIsDefault: options.cycleIsDefault ?? false,
            via: options.via ?? "unknown",
          },
        });
        return;
      }
      track({
        context: AnalyticsContext.FollowUp,
        event: FollowUpEvent.ChangeCycle,
        data: { target: "project", cycle: cycleKey(cycle) },
      });
    },
    [write, track],
  );

  const resetCycle = useCallback(async () => {
    // A cycle clear also turns Updates back on server-side — one write covers
    // both "drop the custom cycle" and "come back from off".
    const ok = await write({ cycle: null }, "Failed to restore the default cycle");
    if (ok) {
      // When the default equals the cycle it replaces, nothing on screen
      // changes, so the toast confirms the write.
      toastManager.add({ title: "Using the default cycle", type: "success" });
      track({
        context: AnalyticsContext.FollowUp,
        event: FollowUpEvent.ResetCycle,
        data: { target: "project" },
      });
    }
  }, [write, track]);

  const endFollowUp = useCallback(
    async (previous: UpdateCycle | null) => {
      const ok = await write({ updatesOff: true }, "Failed to end the Follow up");
      if (!ok) return;
      const hadOwnCycle = previous !== null;
      track({
        context: AnalyticsContext.FollowUp,
        event: FollowUpEvent.End,
        data: { target: "project", hadOwnCycle },
      });
      // Ending is a tracking preference, not a business outcome, so it asks
      // nothing and offers the way back instead: the one sanctioned neutral
      // action toast (no `type`), its Undo pressed or fired by ⌘Z.
      const toastId = toastManager.add({
        title: "Follow up ended",
        actionProps: {
          children: "Undo",
          onClick: () => {
            void write(
              previous
                ? { cycle: { amount: previous.amount, step: previous.step } }
                : { cycle: null },
              "Failed to restart the Follow up",
            ).then((restored) => {
              if (!restored) return;
              track({
                context: AnalyticsContext.FollowUp,
                event: FollowUpEvent.UndoEnd,
                data: { target: "project", hadOwnCycle },
              });
            });
            toastManager.close(toastId);
          },
        },
        data: { actionShortcut: UNDO_TOAST_SHORTCUT },
        timeout: UNDO_TOAST_TIMEOUT_MS,
      });
    },
    [write, track],
  );

  return { saveCycle, resetCycle, endFollowUp };
}
